import React, { useEffect, useState } from "react";
import { TicketIcon } from "@heroicons/react/24/outline";
import { Link, useSearchParams } from "react-router-dom";

import Modal from "../../components/Modal";
import { useToast } from "../../contexts/ToastContext";
import { authService } from "../../services/auth";
import { billingService } from "../../services/billing";
import { getApiErrorMessage } from "../../utils/axios";

const PLAN_LABELS = {
  blueprint: "Blueprint",
  launch: "Launch",
  builder: "Builder",
  factory: "Factory",
  enterprise: "Enterprise",
};

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const Billing = ({ currentUser, setCurrentUser }) => {
  const { error: toastError, success: toastSuccess } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [siteProduct, setSiteProduct] = useState(null);
  const [purchased, setPurchased] = useState([]);
  const [busy, setBusy] = useState("");
  const [billingCycle, setBillingCycle] = useState(
    currentUser?.subscription?.billing_cycle === "yearly" ? "yearly" : "monthly"
  );
  const [pendingChange, setPendingChange] = useState(null);
  const limits = currentUser?.limits || {};
  const subscription = currentUser?.subscription;
  const planKey = limits.key || "blueprint";
  const hasActiveSubscription = Boolean(subscription?.active);
  const unlinkedCredits = purchased.filter((row) => !row.linked);
  const linkedCredits = purchased.filter((row) => row.linked);

  useEffect(() => {
    billingService.getPlans((res) => {
      setPlans(res.data.plans || []);
      setAddons(res.data.addons || []);
      setSiteProduct(res.data.site || null);
    });
    billingService.listPurchasedSites((res) => setPurchased(res.data || []));
  }, []);

  useEffect(() => {
    const cancelled = searchParams.get("success") === "cancel";
    const checkoutOk = searchParams.get("checkout") === "success";
    if (!cancelled && !checkoutOk) return;

    authService.fetchUser((userRes) => {
      if (setCurrentUser) setCurrentUser(userRes.data);
    });
    if (cancelled) {
      toastSuccess("Your plan has been cancelled");
    } else {
      toastSuccess("Payment successful");
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, setCurrentUser, toastSuccess]);

  useEffect(() => {
    if (window.location.hash === "#handoff" || window.location.hash === "#plans") {
      const id = window.location.hash === "#plans" ? "plans" : "handoff";
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  }, [purchased, siteProduct]);

  const checkout = (orderType, cycle) => {
    setBusy(orderType);
    billingService.checkout(
      { order_type: orderType, billing_cycle: cycle },
      (res) => {
        if (res.data?.url) {
          window.location = res.data.url;
        } else {
          setBusy("");
          toastError("No checkout URL returned");
        }
      },
      (err) => {
        setBusy("");
        toastError(getApiErrorMessage(err, "Could not start checkout"));
      },
    );
  };

  const selectPlan = (plan, cycle) => {
    if (hasActiveSubscription) {
      setPendingChange({
        orderType: plan.key,
        billingCycle: cycle,
        planName: plan.name,
      });
      return;
    }
    checkout(plan.key, cycle);
  };

  const confirmChangePlan = () => {
    if (!pendingChange || busy) return;
    const { orderType, billingCycle: cycle, planName } = pendingChange;
    setBusy(orderType);
    billingService.changePlan(
      { order_type: orderType, billing_cycle: cycle },
      () => {
        authService.fetchUser((userRes) => {
          if (setCurrentUser) setCurrentUser(userRes.data);
        });
        setBusy("");
        setPendingChange(null);
        toastSuccess(`Switched to ${planName}`);
      },
      (err) => {
        setBusy("");
        toastError(getApiErrorMessage(err, "Could not change your plan"));
      },
    );
  };

  const openBillingPortal = () => {
    setBusy("portal");
    billingService.portal(
      (res) => {
        if (res.data?.url) {
          window.location = res.data.url;
        } else {
          setBusy("");
          toastError("Could not open billing portal");
        }
      },
      (err) => {
        setBusy("");
        toastError(getApiErrorMessage(err, "Could not open billing portal"));
      },
    );
  };

  const nextBill = formatDate(subscription?.next_bill_at);
  const planName = PLAN_LABELS[subscription?.key || planKey] || planKey;

  return (
    <div className="max-w-4xl space-y-10">
      {hasActiveSubscription && (
        <section className="overflow-hidden rounded-xl border border-theme-secondary">
          <div className="space-y-3 p-6">
            <p className="text-xs font-semibold uppercase tracking-wide opacity-50">
              Your subscription
            </p>
            <h2 className="text-xl font-bold">{planName}</h2>
            <div className="space-y-1 text-sm opacity-70">
              <p className="capitalize">
                Billing cycle: {subscription.billing_cycle || "monthly"}
              </p>
              {subscription.status && (
                <p className="capitalize">Status: {subscription.status}</p>
              )}
              {nextBill && <p>Next bill: {nextBill}</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 border-t border-theme-secondary px-6 py-4">
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={openBillingPortal}
              className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
            >
              {busy === "portal" ? "Opening…" : "Billing portal"}
            </button>
            <Link
              to="/account/billing/cancel/"
              className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold"
            >
              Cancel plan
            </Link>
          </div>
        </section>
      )}

      <section id="plans" className="scroll-mt-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Subscription Plans</h2>
          <div className="inline-flex rounded-lg border border-theme-secondary p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`rounded-md px-3 py-1.5 ${
                billingCycle === "monthly"
                  ? "bg-theme-primary text-theme-button"
                  : "opacity-60 hover:opacity-100"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("yearly")}
              className={`rounded-md px-3 py-1.5 ${
                billingCycle === "yearly"
                  ? "bg-theme-primary text-theme-button"
                  : "opacity-60 hover:opacity-100"
              }`}
            >
              Yearly
            </button>
          </div>
        </div>
        <div className="grid items-stretch gap-4 md:grid-cols-3">
          {plans.map((plan) => {
            const isYearly = billingCycle === "yearly";
            const price = isYearly
              ? (plan.prices?.annual_discounted ?? plan.prices?.annual)
              : (plan.prices?.monthly_discounted ?? plan.prices?.monthly);
            const list = isYearly ? plan.prices?.annual : plan.prices?.monthly;
            const isCurrent =
              plan.key === planKey
              && (subscription?.billing_cycle || "monthly") === billingCycle;
            return (
              <div
                key={plan.key}
                className={`flex h-full flex-col rounded-xl border p-5 ${
                  isCurrent ? "border-theme-primary" : "border-theme-secondary"
                }`}
              >
                <h3 className="font-bold">{plan.name}</h3>
                <p className="mt-1 text-xs opacity-60" title={plan.description}>
                  {plan.description}
                </p>
                <div className="mt-3 flex items-baseline gap-2">
                  {price == null ? (
                    <span className="text-xl font-bold">Free</span>
                  ) : (
                    <>
                      <span className="text-xl font-bold">${price}</span>
                      {list && list !== price && (
                        <span className="text-sm line-through opacity-50">${list}</span>
                      )}
                      <span className="text-sm opacity-60">{isYearly ? "/yr" : "/mo"}</span>
                    </>
                  )}
                </div>
                <ul className="mt-3 flex-1 space-y-1 text-xs opacity-70">
                  {(plan.features || []).slice(0, 6).map((f) => (
                    <li key={f.name}>· {f.name}</li>
                  ))}
                </ul>
                <div className="mt-4">
                  {isCurrent ? (
                    <p className="py-2 text-center text-xs font-semibold opacity-60">
                      Your current plan
                    </p>
                  ) : plan.purchasable ? (
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => selectPlan(plan, billingCycle)}
                      className="w-full rounded-lg bg-theme-primary px-3 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
                    >
                      {busy === plan.key
                        ? (hasActiveSubscription ? "Updating…" : "Redirecting…")
                        : (hasActiveSubscription ? `Switch to ${plan.name}` : `Get ${plan.name}`)}
                    </button>
                  ) : (
                    <div className="h-9" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {!hasActiveSubscription && (
        <section id="handoff" className="scroll-mt-8">
          <h2 className="mb-4 text-sm font-semibold">
            {siteProduct?.name || "I'll take it from here"}
          </h2>
          <div className="rounded-xl border border-theme-secondary p-6">
            <p className="text-sm opacity-70">
              Want to code the rest of your site yourself? Download your data and codebase,
              then host the website yourself.
            </p>
            <ul className="mt-3 space-y-1 text-xs opacity-70">
              {(siteProduct?.features || []).map((f) => (
                <li key={f.name}>· {f.name}</li>
              ))}
            </ul>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-2xl font-bold">
                ${siteProduct?.prices?.price_discounted ?? 25}
              </span>
              <span className="text-sm line-through opacity-50">
                ${siteProduct?.prices?.price ?? 50}
              </span>
              <span className="text-sm opacity-60">one-time</span>
            </div>

            {unlinkedCredits.length > 0 && (
              <Link
                to="/account/billing/transfer/"
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-theme-primary hover:underline"
              >
                <TicketIcon className="h-4 w-4" />
                You have {unlinkedCredits.length} unused transfer{" "}
                {unlinkedCredits.length === 1 ? "credit" : "credits"}.
              </Link>
            )}
            {linkedCredits.length > 0 && (
              <p className="mt-2 text-xs opacity-60">
                Transferred:{" "}
                {linkedCredits.map((row) => row.project_name || "Site").join(", ")}
              </p>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              {siteProduct?.purchasable && (
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => checkout("site")}
                  className="rounded-lg bg-theme-primary px-5 py-2.5 text-sm font-semibold text-theme-button disabled:opacity-50"
                >
                  {busy === "site" ? "Redirecting…" : "Buy single site"}
                </button>
              )}
              {unlinkedCredits.length > 0 && (
                <Link
                  to="/account/billing/transfer/"
                  className="rounded-lg border border-theme-secondary px-5 py-2.5 text-sm font-semibold"
                >
                  Transfer site
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-4 text-sm font-semibold">Add-ons</h2>
        <div className="grid items-stretch gap-4 md:grid-cols-3">
          {addons.map((addon) => (
            <div
              key={addon.key}
              className="flex h-full flex-col rounded-xl border border-theme-secondary p-5"
            >
              <h3 className="font-bold">{addon.name}</h3>
              <p className="mt-1 min-h-[2.5rem] text-xs opacity-60">{addon.description}</p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-xl font-bold">${addon.price}</span>
                <span className="text-sm opacity-60">
                  {addon.period === "monthly" ? "/mo" : "one-time"}
                </span>
              </div>
              <button
                type="button"
                disabled
                className="mt-auto pt-4 w-full rounded-lg border border-theme-secondary px-3 py-2 text-sm font-semibold opacity-50"
              >
                Coming soon
              </button>
            </div>
          ))}
        </div>
      </section>

      {pendingChange && (
        <Modal
          onClose={() => {
            if (!busy) setPendingChange(null);
          }}
          maxWidth="max-w-md"
          heightClass="h-auto"
        >
          <h3 className="text-lg font-semibold">Change your plan?</h3>
          <p className="mt-2 text-sm opacity-70">
            Switch from {planName} to {pendingChange.planName} (
            {pendingChange.billingCycle})? Your bill will be prorated for the
            rest of this billing period.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={confirmChangePlan}
              className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
            >
              {busy === pendingChange.orderType ? "Updating…" : "Confirm change"}
            </button>
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={() => setPendingChange(null)}
              className="rounded-lg border border-theme-secondary px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Billing;
